import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import ReactMarkdown from 'react-markdown';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// Create anonymous Supabase client for KVKK page (no auth required)
const SUPABASE_URL = "https://yckhhzgqlfgwcwfmugow.supabase.co";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlja2hoemdxbGZnd2N3Zm11Z293Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY0MTQwMzgsImV4cCI6MjA3MTk5MDAzOH0.kAcPWINVmWEB8uIZL3qp8fGBXoiIrVrEBPj9LRfpUJA";

const supabase = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
  },
});

const KvkkConsentPage = () => {
  const { requestId, token } = useParams<{ requestId: string; token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [policyTitle, setPolicyTitle] = useState<string | null>(null);
  const [policyContent, setPolicyContent] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [decisionResult, setDecisionResult] = useState<'grant' | 'reject' | null>(null);

  useEffect(() => {
    const fetchRequestInfo = async () => {
      console.log('🔗 [KVKK Page] ========================================');
      console.log('🔗 [KVKK Page] Component mounted/updated');
      console.log('🔗 [KVKK Page] requestId from URL:', requestId);
      console.log('🔗 [KVKK Page] token from URL:', token);
      console.log('🔗 [KVKK Page] Full URL:', window.location.href);
      console.log('🔗 [KVKK Page] URL pathname:', window.location.pathname);
      
      if (!requestId || !token) {
        console.error('❌ [KVKK Page] Missing requestId or token:', { requestId, token });
        setErrorMessage('Geçersiz bağlantı');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setErrorMessage(null);

        console.log('🔗 [KVKK Page] Calling kvkk_get_request_info RPC');
        console.log('🔗 [KVKK Page] Parameters:', {
          p_request_id: requestId,
          p_raw_token: token,
        });
        console.log('🔗 [KVKK Page] Token length:', token?.length);
        console.log('🔗 [KVKK Page] Token type:', typeof token);
        console.log('🔗 [KVKK Page] RequestId length:', requestId?.length);
        console.log('🔗 [KVKK Page] Supabase client:', supabase ? 'exists' : 'missing');

        // Try to decode the token in case it's URL encoded
        const decodedToken = decodeURIComponent(token);
        console.log('🔗 [KVKK Page] Decoded token:', decodedToken);
        console.log('🔗 [KVKK Page] Token matches original?', decodedToken === token);

        // IMPORTANT: The database shows link_token_hash matches the token exactly
        // This suggests the DB function might be storing raw token instead of hash
        // OR the function expects raw token and compares directly
        // Let's try the token as-is first
        
        let { data, error } = await supabase.rpc('kvkk_get_request_info', {
          p_request_id: requestId,
          p_raw_token: token,
        });

        console.log('🔗 [KVKK Page] RPC call completed (original token)');
        console.log('🔗 [KVKK Page] Response data:', data);
        console.log('🔗 [KVKK Page] Response error:', error);

        // If error and token was URL encoded, try with decoded token
        if (error && decodedToken !== token) {
          console.log('🔗 [KVKK Page] Retrying with decoded token...');
          const retryResult = await supabase.rpc('kvkk_get_request_info', {
            p_request_id: requestId,
            p_raw_token: decodedToken,
          });
          
          if (!retryResult.error) {
            console.log('✅ [KVKK Page] Success with decoded token!');
            data = retryResult.data;
            error = null;
          } else {
            console.log('❌ [KVKK Page] Still failed with decoded token:', retryResult.error);
          }
        }
        
        // DEBUG: Check what's actually stored in the database
        // Note: This might fail due to RLS, but let's try
        if (error) {
          console.log('🔍 [KVKK Page] DEBUG: Checking database record...');
          console.log('🔍 [KVKK Page] Error message:', error.message);
          
          // Try to query the table directly (might fail due to RLS)
          const { data: dbRecord, error: dbError } = await supabase
            .from('global_consent_requests')
            .select('id, link_token_hash, link_expires_at, created_at')
            .eq('id', requestId)
            .single();
          
          if (dbError) {
            console.error('🔍 [KVKK Page] Cannot query table directly (RLS or not found):', dbError);
            console.log('🔍 [KVKK Page] This is expected - RPC function should handle this');
          } else if (dbRecord) {
            console.log('🔍 [KVKK Page] Database record found:', dbRecord);
            console.log('🔍 [KVKK Page] Token from URL:', token);
            console.log('🔍 [KVKK Page] Stored hash:', dbRecord.link_token_hash);
            console.log('🔍 [KVKK Page] Tokens match?', token === dbRecord.link_token_hash);
            console.log('🔍 [KVKK Page] Link expired?', new Date(dbRecord.link_expires_at) < new Date());
          }
          
          // The error "request_not_found" means either:
          // 1. The request ID doesn't exist in the database
          // 2. The RPC function has RLS that's blocking access
          // 3. The RPC function needs SECURITY DEFINER to bypass RLS
          console.log('🔍 [KVKK Page] Possible causes:');
          console.log('  - Request ID does not exist:', requestId);
          console.log('  - RPC function kvkk_get_request_info might need SECURITY DEFINER');
          console.log('  - RLS policies might be blocking anonymous access');
        }

        if (error) {
          console.error('❌ [KVKK Page] Error fetching KVKK request info:', error);
          console.error('❌ [KVKK Page] Error code:', error.code);
          console.error('❌ [KVKK Page] Error message:', error.message);
          console.error('❌ [KVKK Page] Error details:', error.details);
          console.error('❌ [KVKK Page] Error hint:', error.hint);
          console.error('❌ [KVKK Page] Full error:', JSON.stringify(error, null, 2));
          
          // Check for specific error messages
          const errorMsg = error.message?.toLowerCase() || '';
          const errorDetails = error.details?.toLowerCase() || '';
          const errorHint = error.hint?.toLowerCase() || '';
          const fullErrorText = `${errorMsg} ${errorDetails} ${errorHint}`;
          
          console.log('🔗 [KVKK Page] Checking error text:', fullErrorText);
          
          if (fullErrorText.includes('not found') || fullErrorText.includes('request_not_found')) {
            console.log('🔗 [KVKK Page] Error type: Request not found');
            setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
          } else if (fullErrorText.includes('expired') || fullErrorText.includes('link_expired')) {
            console.log('🔗 [KVKK Page] Error type: Link expired');
            setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
          } else if (fullErrorText.includes('invalid') || fullErrorText.includes('invalid_token')) {
            console.log('🔗 [KVKK Page] Error type: Invalid token');
            setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
          } else {
            console.log('🔗 [KVKK Page] Error type: Unknown');
            setErrorMessage(`Bağlantı bilgileri alınırken bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
          }
          setLoading(false);
          return;
        }

        if (!data) {
          console.error('❌ [KVKK Page] No data returned from RPC');
          setErrorMessage('Bağlantı bilgileri bulunamadı.');
          setLoading(false);
          return;
        }

        // RPC functions that return TABLE return an array, so get the first element
        const requestData = Array.isArray(data) ? data[0] : data;
        
        if (!requestData) {
          console.error('❌ [KVKK Page] Empty array returned from RPC');
          setErrorMessage('Bağlantı bilgileri bulunamadı.');
          setLoading(false);
          return;
        }

        console.log('✅ [KVKK Page] Successfully fetched request info');
        console.log('✅ [KVKK Page] Request data:', requestData);
        console.log('✅ [KVKK Page] Policy title:', requestData.policy_title);
        console.log('✅ [KVKK Page] Policy content length:', requestData.policy_content?.length || 0);
        console.log('✅ [KVKK Page] Policy content preview:', requestData.policy_content?.substring(0, 100));
        console.log('✅ [KVKK Page] Link expires at:', requestData.link_expires_at);
        
        // Check if link is expired
        if (requestData.link_expires_at) {
          const expiresAt = new Date(requestData.link_expires_at);
          const now = new Date();
          console.log('🔗 [KVKK Page] Current time:', now.toISOString());
          console.log('🔗 [KVKK Page] Expires at:', expiresAt.toISOString());
          console.log('🔗 [KVKK Page] Is expired?', expiresAt < now);
          console.log('🔗 [KVKK Page] Time difference (ms):', expiresAt.getTime() - now.getTime());
        }

        setPolicyTitle(requestData.policy_title || 'KVKK Aydınlatma Metni');
        setPolicyContent(requestData.policy_content || '');
        setExpiresAt(requestData.link_expires_at || null);
        setLoading(false);
        console.log('🔗 [KVKK Page] ========================================');
      } catch (err) {
        console.error('❌ [KVKK Page] Exception in fetchRequestInfo:', err);
        console.error('❌ [KVKK Page] Exception type:', typeof err);
        console.error('❌ [KVKK Page] Exception details:', err instanceof Error ? {
          name: err.name,
          message: err.message,
          stack: err.stack,
        } : err);
        setErrorMessage('Bağlantı bilgileri alınırken bir hata oluştu.');
        setLoading(false);
      }
    };

    void fetchRequestInfo();
  }, [requestId, token]);

  const handleDecision = async (decision: 'grant' | 'reject') => {
    if (!requestId || !token) {
      setErrorMessage('Geçersiz bağlantı');
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);

    try {
      // Collect evidence: user agent, IP address, date
      const userAgent = navigator.userAgent || 'unknown';
      
      // Try to get IP address (optional - may fail due to CORS or service availability)
      let ipAddress: string | null = null;
      try {
        // Use a simple IP detection service
        const ipResponse = await fetch('https://api.ipify.org?format=json', {
          method: 'GET',
          signal: AbortSignal.timeout(3000), // 3 second timeout
        });
        if (ipResponse.ok) {
          const ipData = await ipResponse.json();
          ipAddress = ipData.ip || null;
        }
      } catch (ipError) {
        // IP detection failed - that's okay, we'll store 'unknown'
        console.log('IP detection failed (non-critical):', ipError);
        ipAddress = null;
      }

      console.log('📝 [KVKK Decision] Submitting decision:', {
        decision,
        requestId,
        userAgent,
        ipAddress: ipAddress || 'unknown',
      });

      const { data, error } = await supabase.rpc('kvkk_handle_decision', {
        p_request_id: requestId,
        p_raw_token: token,
        p_decision: decision,
        p_ip_address: ipAddress,
        p_user_agent: userAgent,
      });

      if (error) {
        console.error('❌ [KVKK Decision] Error handling KVKK decision:', error);
        console.error('❌ [KVKK Decision] Error code:', error.code);
        console.error('❌ [KVKK Decision] Error message:', error.message);
        console.error('❌ [KVKK Decision] Error details:', error.details);
        console.error('❌ [KVKK Decision] Error hint:', error.hint);
        
        // Provide more specific error messages
        const errorMsg = error.message?.toLowerCase() || '';
        if (errorMsg.includes('invalid_token')) {
          setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
        } else if (errorMsg.includes('expired')) {
          setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
        } else if (errorMsg.includes('not_found')) {
          setErrorMessage('Bu bağlantı geçersiz veya süresi dolmuş.');
        } else {
          setErrorMessage('İşleminiz sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        }
        setSubmitting(false);
        return;
      }

      console.log('✅ [KVKK Decision] Success:', data);
      setDecisionResult(decision);
    } catch (err) {
      console.error('❌ [KVKK Decision] Exception in handleDecision:', err);
      setErrorMessage('İşleminiz sırasında bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Yükleniyor...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (errorMessage && !decisionResult) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (decisionResult === 'grant') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <CheckCircle className="w-16 h-16 text-green-600" />
              <h1 className="text-2xl font-bold">Teşekkür ederiz!</h1>
              <p className="text-muted-foreground">
                KVKK kapsamında açık rıza onayınız alınmıştır.
              </p>
              <p className="text-sm text-muted-foreground">
                Onayınızı dilediğiniz zaman bizimle iletişime geçerek geri çekebilirsiniz.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (decisionResult === 'reject') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              <XCircle className="w-16 h-16 text-orange-600" />
              <h1 className="text-2xl font-bold">Tercihiniz kaydedildi</h1>
              <p className="text-muted-foreground">
                Tercihiniz "Onay vermiyorum" olarak kaydedildi.
              </p>
              <p className="text-sm text-muted-foreground">
                Kanunen zorunlu işlemler için verileriniz işlenmeye devam edebilir.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              <CardTitle className="text-2xl">{policyTitle || 'KVKK Aydınlatma Metni'}</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {errorMessage && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            <div className="prose prose-sm max-w-none dark:prose-invert prose-headings:font-semibold prose-p:text-foreground prose-strong:font-semibold">
              {policyContent ? (
                <ReactMarkdown>{policyContent}</ReactMarkdown>
              ) : (
                <p className="text-muted-foreground">Politika içeriği yükleniyor...</p>
              )}
            </div>

            {expiresAt && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Bu bağlantı {new Date(expiresAt).toLocaleString('tr-TR')} tarihine kadar geçerlidir.
                </AlertDescription>
              </Alert>
            )}

            <div className="border-t pt-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Onay vermemeniz, kanunen zorunlu olmayan süreçler için verilerinizin kullanılmayacağı anlamına gelir.
                Fatura, yasal yükümlülükler gibi zorunlu işlemler için verileriniz işlenmeye devam edebilir.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-stretch">
                <Button
                  onClick={() => handleDecision('grant')}
                  disabled={submitting}
                  className="flex-[2] h-16 text-lg font-semibold shadow-lg hover:shadow-xl transition-shadow"
                  size="lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-6 h-6 mr-2" />
                      Onaylıyorum
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleDecision('reject')}
                  disabled={submitting}
                  variant="outline"
                  className="flex-1 h-12 text-base"
                  size="lg"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      İşleniyor...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 mr-2" />
                      Onay vermiyorum
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default KvkkConsentPage;

